/**
 * The ICE route a runner link settled on, in the shape every side reports it
 * and the server counts it (`IceLinkStat`). It exists to answer one question
 * with data: how often does a link actually land on a TURN relay.
 */

/** The ICE route a WebRTC link settled on. */
export type WebRtcPath = 'direct' | 'stun' | 'turn';

/** Map a settled candidate pair to the path it represents. A relay on either
 *  side means the traffic goes through TURN; a reflexive address on either
 *  side means STUN found a way across a NAT; anything else is on-network. */
export function pathFromPair(local: string, remote: string): WebRtcPath {
  if (local === 'relay' || remote === 'relay') return 'turn';
  if (
    local === 'srflx' ||
    remote === 'srflx' ||
    local === 'prflx' ||
    remote === 'prflx'
  ) {
    return 'stun';
  }
  return 'direct';
}

export const ICE_UNKNOWN = 'unknown';
const CANDIDATE_TYPES = ['host', 'srflx', 'prflx', 'relay'];
const PROTOCOLS = ['udp', 'tcp', 'tls'];

/** The winning candidate pair as its reporter saw it. `protocol` is how the
 *  reporter's own candidate reaches the network: for a relay candidate, the
 *  transport to the TURN server. */
export type IcePair = {
  local: string;
  remote: string;
  protocol: string | null;
};

const oneOf = (allowed: string[], value: unknown): string => {
  if (typeof value !== 'string') return ICE_UNKNOWN;
  const lower = value.toLowerCase();
  // node-datachannel names transports `UDP`, `TCP_active`, `TCP_passive`.
  return allowed.find((item) => lower.startsWith(item)) ?? ICE_UNKNOWN;
};

/**
 * Reduce a reported pair to the fixed vocabulary the counters are keyed by. A
 * report is client input, and its values become part of a primary key, so
 * anything outside the vocabulary is `unknown` rather than a new row.
 */
export function normalizeIcePair(pair: unknown): {
  local: string;
  remote: string;
  protocol: string;
} {
  const raw =
    typeof pair === 'object' && pair !== null
      ? (pair as Record<string, unknown>)
      : {};
  return {
    local: oneOf(CANDIDATE_TYPES, raw.local),
    remote: oneOf(CANDIDATE_TYPES, raw.remote),
    protocol: oneOf(PROTOCOLS, raw.protocol)
  };
}

/** What carried a settled link, for a table row: `Direct`, `STUN`, `TURN`,
 *  `Server relay` (the socket fallback) or `Unknown` (the platform reported no
 *  nominated pair — react-native-webrtc never does). */
export type IceRoute = WebRtcPath | 'socket' | typeof ICE_UNKNOWN;

export function routeOf(
  transport: string,
  local: string,
  remote: string
): IceRoute {
  if (transport === 'socket') return 'socket';
  if (local === ICE_UNKNOWN || remote === ICE_UNKNOWN) return ICE_UNKNOWN;
  return pathFromPair(local, remote);
}

export type IceLinkSource = 'web' | 'mobile' | 'runner';
export type IceLinkOutcome = 'connected' | 'failed';
export type IceLinkTransport = 'webrtc' | 'socket';

export type IceLinkKey = {
  source: IceLinkSource;
  outcome: IceLinkOutcome;
  transport: IceLinkTransport;
  local: string;
  remote: string;
  protocol: string;
};

/**
 * The counter a report belongs to. `pair` is whatever the reporter sent, so it
 * is reduced to the fixed vocabulary here; a report with no pair (the socket
 * relay, a failed handshake, every phone) counts under `unknown`.
 */
export const iceLinkKey = (report: {
  source: IceLinkSource;
  outcome: IceLinkOutcome;
  transport: IceLinkTransport;
  pair: unknown;
}): IceLinkKey => ({
  source: report.source,
  outcome: report.outcome,
  transport: report.transport,
  ...(report.transport === 'socket'
    ? { local: ICE_UNKNOWN, remote: ICE_UNKNOWN, protocol: ICE_UNKNOWN }
    : normalizeIcePair(report.pair))
});

export type IceLinkRow = IceLinkKey & { count: number };

export type IceLinkSummary = {
  source: IceLinkSource;
  /** Links that came up, by what carried them. */
  connected: Record<IceRoute, number>;
  connectedTotal: number;
  /** Handshakes reported failed. A link that opened and later dropped is in
   *  both this and `connected`, so this is not a failure rate on its own. */
  failed: number;
  /** Of the links whose route is known, the share on a TURN relay. Null until
   *  at least one such link has been counted. */
  turnShare: number | null;
};

const emptyRoutes = (): Record<IceRoute, number> => ({
  direct: 0,
  stun: 0,
  turn: 0,
  socket: 0,
  unknown: 0
});

/** Roll counter rows up to one line per reporting side. */
export const summarizeIceLinks = (rows: IceLinkRow[]): IceLinkSummary[] => {
  const bySource = new Map<IceLinkSource, IceLinkSummary>();
  for (const row of rows) {
    const summary = bySource.get(row.source) ?? {
      source: row.source,
      connected: emptyRoutes(),
      connectedTotal: 0,
      failed: 0,
      turnShare: null
    };
    if (row.outcome === 'failed') {
      summary.failed += row.count;
    } else {
      summary.connected[routeOf(row.transport, row.local, row.remote)] +=
        row.count;
      summary.connectedTotal += row.count;
    }
    bySource.set(row.source, summary);
  }
  return [...bySource.values()].map((summary) => {
    const { direct, stun, turn } = summary.connected;
    const known = direct + stun + turn;
    return { ...summary, turnShare: known > 0 ? turn / known : null };
  });
};
